import { memo, useEffect, useRef, useState, type ReactElement } from "react";
import { Check, CheckCheck, Copy, CornerUpLeft, Download, Edit3, FileText, Forward, History, Info, ListChecks, Lock, MoreHorizontal, Phone, PhoneMissed, Pin, PinOff, Star, StarOff, Trash2, Video, Ban, Reply, Link2 } from "lucide-react";
import type { Member, Message } from "./types";
import { Avatar, RichText, useChat } from "./ui";
import VoicePlayer from "./VoicePlayer";
import { fileUrl } from "./chatConfig";
import { fmtDateTime, fmtDuration, fmtSize, fmtTime } from "./jalali";

export type MsgAction = "reply" | "reply_private" | "copy" | "forward" | "star" | "pin" | "edit" | "delete" | "history" | "readers" | "task" | "jump" | "image" | "join_call";

interface Props {
  msg: Message; mine: boolean; isGroup: boolean; showHead: boolean; members: Member[]; amAdmin: boolean; highlight: boolean;
  onAction: (a: MsgAction, m: Message) => void;
}

function Ticks({ msg, members, isGroup }: { msg: Message; members: Member[]; isGroup: boolean }) {
  if (msg._pending) return <span className="cx-tick pending" title="در حال ارسال">◔</span>;
  if (msg._failed) return <span className="cx-tick failed" title="ارسال نشد">!</span>;
  const others = members.filter(m => m.id !== msg.sender_id);
  const readBy = others.filter(m => (m.last_read_id || 0) >= msg.id).length;
  const all = others.length > 0 && readBy >= others.length;
  if (readBy === 0) return <span className="cx-tick"><Check size={14} /></span>;
  return <span className={`cx-tick ${all ? "read" : "partial"}`} title={`${readBy} از ${others.length}`}><CheckCheck size={14} />{isGroup && !all && <small>{readBy}/{others.length}</small>}</span>;
}

function CallCard({ msg, mine, onAction }: { msg: Message; mine: boolean; onAction: Props["onAction"] }) {
  const { roomById } = useChat();
  const c = msg.call; const video = c?.type === "video";
  const room = roomById(msg.room_id); const live = room?.active_call && c && room.active_call.id === c.id;
  const missed = c && (c.status === "missed" || c.status === "declined");
  const Icon = missed ? PhoneMissed : video ? Video : Phone;
  let label = video ? "تماس تصویری" : "تماس صوتی";
  let sub = c ? (live ? "در جریان…" : c.status === "ended" ? `مدت: ${fmtDuration(c.duration)}` : c.status === "declined" ? "رد شد" : c.status === "missed" ? (mine ? "بی‌پاسخ" : "تماس بی‌پاسخ") : "در حال زنگ…") : "";
  return (
    <div className={`cx-callcard ${missed ? "missed" : ""} ${live ? "live" : ""}`}>
      <span className="cx-callcard-ic"><Icon size={18} /></span>
      <div><b>{label}</b><small>{sub}</small></div>
      {live && <button className="cx-btn sm primary" onClick={() => onAction("join_call", msg)}>پیوستن</button>}
    </div>
  );
}

function MessageBubbleInner({ msg, mine, isGroup, showHead, members, amAdmin, highlight, onAction }: Props) {
  const { me, roomById } = useChat();
  const [menu, setMenu] = useState(false);
  const [showOrig, setShowOrig] = useState(false);
  const [up, setUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, [menu]);

  if (msg.type === "system") return <div className="cx-sys"><span>{msg.content}</span></div>;

  const act = (a: MsgAction) => { setMenu(false); onAction(a, msg); };
  const mentionedMe = msg.mentions?.some(m => m.id === me.id);
  const isMedia = msg.type === "image" || msg.type === "file" || msg.type === "voice";
  const caption = isMedia && msg.content && msg.content !== msg.file_name && msg.type !== "voice" ? msg.content : "";
  const room = roomById(msg.room_id);

  // ─── پیام حذف‌شده: همیشه شفاف و قابل مشاهده برای همه ───
  if (msg.is_deleted) {
    return (
      <div className={`cx-row ${mine ? "mine" : ""}`} id={`m-${msg.id}`}>
        <div className={`cx-ledger ${highlight ? "hl" : ""}`}>
          <div className="cx-ledger-head"><Ban size={14} /><b>{msg.deleted_by_name || msg.sender_name}</b> این پیام را حذف کرد<span>{fmtDateTime(msg.deleted_at)}</span></div>
          <div className="cx-ledger-body">
            {msg.type === "voice" ? <span>🎤 پیام صوتی ({fmtDuration(msg.duration)})</span>
              : msg.type === "image" || msg.type === "file" ? <a href={fileUrl(msg.file_url)} target="_blank" rel="noreferrer noopener"><FileText size={14} /> {msg.file_name}</a>
              : showOrig || (msg.content || "").length < 140 ? <span><RichText text={msg.content || ""} /></span>
              : <span>{(msg.content || "").slice(0, 140)}… <button className="cx-linkbtn" onClick={() => setShowOrig(true)}>نمایش کامل</button></span>}
          </div>
          <div className="cx-ledger-foot"><Lock size={11} /> متن اصلی برای همه محفوظ است<span>{fmtTime(msg.created_at)}</span></div>
        </div>
      </div>
    );
  }

  const menuItems: { k: MsgAction; label: string; icon: ReactElement; danger?: boolean; show?: boolean }[] = [
    { k: "reply", label: "پاسخ", icon: <Reply size={15} />, show: msg.type !== "call" },
    { k: "reply_private", label: "پاسخ خصوصی", icon: <CornerUpLeft size={15} />, show: isGroup && !mine && msg.type !== "call" },
    { k: "copy", label: "کپی متن", icon: <Copy size={15} />, show: msg.type === "text" },
    { k: "forward", label: "فوروارد", icon: <Forward size={15} />, show: msg.type !== "call" },
    { k: "star", label: msg.is_starred ? "برداشتن ستاره" : "ستاره‌دار", icon: msg.is_starred ? <StarOff size={15} /> : <Star size={15} /> },
    { k: "pin", label: msg.is_pinned ? "برداشتن پین" : "پین پیام", icon: msg.is_pinned ? <PinOff size={15} /> : <Pin size={15} />, show: (!isGroup || amAdmin) && msg.type !== "call" },
    { k: "task", label: "ثبت وظیفه از این پیام", icon: <ListChecks size={15} />, show: msg.type === "text" },
    { k: "readers", label: "اطلاعات پیام", icon: <Info size={15} />, show: mine },
    { k: "edit", label: "ویرایش", icon: <Edit3 size={15} />, show: mine && msg.type === "text" },
    { k: "delete", label: "حذف", icon: <Trash2 size={15} />, danger: true, show: mine && msg.type !== "call" },
  ];

  return (
    <div className={`cx-row ${mine ? "mine" : ""} ${showHead ? "head" : ""}`} id={`m-${msg.id}`}>
      {!mine && isGroup && (showHead ? <Avatar name={msg.sender_name} src={msg.sender_avatar} size={32} /> : <span style={{ width: 32, flexShrink: 0 }} />)}
      <div ref={ref} className={`cx-bubble ${mine ? "mine" : ""} ${showHead ? "tail" : ""} ${highlight ? "hl" : ""} ${mentionedMe ? "mentioned" : ""} ${msg.type === "image" && !caption ? "img-only" : ""}`}>
        <button className="cx-bubble-menu-btn" onClick={() => { const r = ref.current?.getBoundingClientRect(); setUp(!!r && window.innerHeight - r.top < 380); setMenu(m => !m); }} aria-label="گزینه‌ها"><MoreHorizontal size={16} /></button>
        {menu && (
          <div className={`cx-menu ${up ? "up" : ""}`} role="menu">
            {menuItems.filter(i => i.show !== false).map(i => (
              <button key={i.k} className={i.danger ? "danger" : ""} onClick={() => act(i.k)}>{i.icon}{i.label}</button>
            ))}
          </div>
        )}
        {!mine && isGroup && showHead && <div className="cx-sender">{msg.sender_name}</div>}
        {msg.forwarded_from_name && <div className="cx-fwd"><Forward size={12} /> فوروارد شده از {msg.forwarded_from_name}</div>}
        {msg.reply && (
          <button className="cx-quote" onClick={() => msg.reply!.room_id === msg.room_id && onAction("jump", { ...msg, id: msg.reply!.id })}>
            <b>{msg.reply.sender_name}{msg.reply.room_id !== msg.room_id && msg.reply.room_name ? ` • از گروه ${msg.reply.room_name}` : ""}</b>
            <span>{msg.reply.is_deleted ? "🚫 " : ""}{msg.reply.type === "voice" ? "🎤 پیام صوتی" : msg.reply.type === "image" ? "🖼 عکس" : msg.reply.type === "file" ? "📎 فایل" : (msg.reply.content || "").slice(0, 90)}</span>
          </button>
        )}
        {msg.task_id && <div className="cx-chip task"><ListChecks size={13} /> وظیفه: {msg.task_title || `#${msg.task_id}`}</div>}
        {msg.ref_type && msg.ref_type !== "call" && <div className="cx-chip ref"><Link2 size={13} /> {msg.ref_type} {msg.ref_id}</div>}

        {msg.type === "call" ? <CallCard msg={msg} mine={mine} onAction={onAction} />
          : msg.type === "voice" ? <VoicePlayer src={fileUrl(msg.file_url)} duration={msg.duration} waveform={msg.waveform} mine={mine} seed={msg.id} />
          : msg.type === "image" ? <img className="cx-img" src={fileUrl(msg.file_url)} alt={msg.file_name || "تصویر"} loading="lazy" onClick={() => onAction("image", msg)} />
          : msg.type === "file" ? (
            <a className="cx-file" href={fileUrl(msg.file_url)} download={msg.file_name || true} target="_blank" rel="noreferrer noopener">
              <span className="cx-file-ic"><FileText size={20} /></span>
              <span className="cx-file-info"><b>{msg.file_name}</b><small>{fmtSize(msg.file_size)}</small></span>
              <Download size={16} />
            </a>
          ) : <div className="cx-text"><RichText text={msg.content || ""} mentions={msg.mentions} /></div>}
        {caption && <div className="cx-text cap"><RichText text={caption} mentions={msg.mentions} /></div>}

        <div className="cx-meta">
          {msg.is_pinned ? <Pin size={11} /> : null}
          {msg.is_starred ? <Star size={11} fill="currentColor" /> : null}
          {msg.is_edited ? <button className="cx-edited" onClick={() => onAction("history", msg)} title="مشاهده تاریخچه ویرایش"><History size={11} /> ویرایش‌شده</button> : null}
          <span>{fmtTime(msg.created_at)}</span>
          {mine && msg.type !== "call" && <button className="cx-ticks-btn" onClick={() => !msg._pending && onAction("readers", msg)}><Ticks msg={msg} members={members} isGroup={isGroup} /></button>}
        </div>
      </div>
    </div>
  );
}
export default memo(MessageBubbleInner, (a, b) => a.msg === b.msg && a.mine === b.mine && a.showHead === b.showHead && a.highlight === b.highlight && a.members === b.members && a.amAdmin === b.amAdmin);
